import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import Home from "@/pages/Home";

// Everything below the homepage is code-split so the first load stays fast
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Trial = lazy(() => import("@/pages/Trial"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const Account = lazy(() => import("@/pages/Account"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const AccountDeletion = lazy(() => import("@/pages/AccountDeletion"));
const Support = lazy(() => import("@/pages/Support"));
const Resources = lazy(() => import("@/pages/Resources"));
const ResourceDetail = lazy(() => import("@/pages/ResourceDetail"));
const Roadmap = lazy(() => import("@/pages/Roadmap"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminResources = lazy(() => import("@/pages/admin/AdminResources"));
const ResourceEditor = lazy(() => import("@/pages/admin/ResourceEditor"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminLeads = lazy(() => import("@/pages/admin/AdminLeads"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));

const ScrollManager = () => {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        const lenis = window.__lenis;
        if (hash) {
            const t = setTimeout(() => {
                const el = document.querySelector(hash);
                if (!el) return;
                if (lenis) lenis.scrollTo(el, { offset: -72 });
                else el.scrollIntoView();
            }, 80);
            return () => clearTimeout(t);
        }
        if (lenis) lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [pathname, hash]);
    return null;
};

function App() {
    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);

    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <ScrollManager />
                    <SiteChrome />
                    <Toaster theme="dark" position="top-center" />
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

const SiteChrome = () => {
    const { pathname } = useLocation();
    const isAdmin = pathname.startsWith("/admin");
    return (
        <>
            {!isAdmin && <Nav />}
            <Suspense fallback={<div className="min-h-screen" data-testid="route-loading" />}>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/trial" element={<Trial />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/account" element={<Account />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/resources/:slug" element={<ResourceDetail />} />
                <Route path="/roadmap" element={<Roadmap />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/account-deletion" element={<AccountDeletion />} />
                <Route path="/support" element={<Support />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="resources" element={<AdminResources />} />
                    <Route path="resources/new" element={<ResourceEditor />} />
                    <Route path="resources/:id" element={<ResourceEditor />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="leads" element={<AdminLeads />} />
                    <Route path="settings" element={<AdminSettings />} />
                </Route>
            </Routes>
            </Suspense>
            {!isAdmin && <Footer />}
        </>
    );
};

export default App;
